import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { uploadFile, buildKey, isUploadConfigured } from '../data/uploadService';
import { apiPost, isBackendConfigured } from '../utils/backendApi';
import { captureScreen } from '../utils/captureScreen';
import { currentBuild } from '../utils/buildFreshness';
import { IS_UAT } from '../data/env';

// "Report a bug" / "Request a feature" for any signed-in member, mounted
// once by App.js on both hosts and opened from the account menu / ⌘K /
// `openBugReport()` / `openFeatureRequest()`. Files go to the raw bucket
// under items/<kind>/<uid>/ through the normal presigned path; the report
// itself is POST /bugs (api/bugsApi.js) or POST /features
// (api/featuresApi.js), which mail the super admin a review link. Route,
// org, app focus, bundle, user agent and viewport ride along automatically
// so the reviewer (and the repo agent) know where it came from.
export const openBugReport = (detail = {}) => window.dispatchEvent(new CustomEvent('nv:report-bug', { detail: { ...detail, kind: 'bug' } }));
export const openFeatureRequest = (detail = {}) => window.dispatchEvent(new CustomEvent('nv:report-bug', { detail: { ...detail, kind: 'feature' } }));

const KINDS = {
  bug: {
    heading: 'Report a bug',
    titleLabel: 'What went wrong?',
    titlePlaceholder: 'Short title',
    descLabel: 'What did you expect, and what happened instead?',
    descPlaceholder: 'Steps, if you have them. Paste a screenshot here too.',
    problemLabel: null,
    attachHelp: 'Screenshots, recordings, logs',
    capture: true,
    endpoint: '/bugs',
    prefix: 'items/bug-reports/',
    submit: 'Send report',
    filedEvent: 'nv:bug-filed',
    thanks: 'We will email you when the fix is live. Follow it under',
    thanksLink: ['/bugs', 'Your reports'],
    another: 'Report another',
    empty: 'Describe the bug or attach a screenshot.',
  },
  feature: {
    heading: 'Request a feature',
    titleLabel: 'What would you like nventr to do?',
    titlePlaceholder: 'Short title',
    descLabel: 'Describe it',
    descPlaceholder: 'What it should do, where it should live, what it should look like.',
    problemLabel: 'What problem does it solve for you?',
    attachHelp: 'Mockups, examples, documents',
    capture: false,
    endpoint: '/features',
    prefix: 'items/feature-requests/',
    submit: 'Send request',
    filedEvent: 'nv:feature-filed',
    thanks: 'It is on the board now — others can vote for it, and we will email you when it is triaged and when it ships. See',
    thanksLink: ['/features', 'the board'],
    another: 'Request another',
    empty: 'Give the feature a title or describe it.',
  },
};

const MAX_FILES = 5;
const MAX_BYTES = 8 * 1024 * 1024;
const ACCEPT = 'image/*,.txt,.log,.md,.json,.csv,.pdf,.docx,.mov,.mp4,.webm';

const fmtBytes = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function BugReportDialog() {
  const { user } = useAuth();
  const member = !!user && !user.isAnonymous;
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [problem, setProblem] = useState('');
  const [files, setFiles] = useState([]); // [{ file, name, type, size, preview }]
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    const onOpen = (e) => {
      if (!member) return;
      setKind(e.detail?.kind === 'feature' ? 'feature' : 'bug');
      setOpen(true);
      setDone(null);
      setError('');
      if (e.detail?.title) setTitle(String(e.detail.title));
      if (e.detail?.description) setDescription(String(e.detail.description));
    };
    window.addEventListener('nv:report-bug', onOpen);
    return () => window.removeEventListener('nv:report-bug', onOpen);
  }, [member]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) setOpen(false); };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => titleRef.current?.focus(), 30);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(t); };
  }, [open, busy]);

  // Revoke image previews on unmount only (removeFile revokes as it goes).
  const filesRef = useRef(files);
  useEffect(() => { filesRef.current = files; }, [files]);
  useEffect(() => () => filesRef.current.forEach((f) => f.preview && URL.revokeObjectURL(f.preview)), []);

  if (!member) return null;

  const K = KINDS[kind];

  const addFiles = (list) => {
    setError('');
    const incoming = Array.from(list || []);
    setFiles((prev) => {
      const next = [...prev];
      for (const file of incoming) {
        if (next.length >= MAX_FILES) { setError(`Up to ${MAX_FILES} files per report.`); break; }
        if (file.size > MAX_BYTES) { setError(`${file.name} is over ${fmtBytes(MAX_BYTES)}.`); continue; }
        next.push({
          file, name: file.name, type: file.type || '', size: file.size,
          preview: /^image\//.test(file.type) ? URL.createObjectURL(file) : null,
        });
      }
      return next;
    });
  };

  const removeFile = (i) => setFiles((prev) => {
    if (prev[i]?.preview) URL.revokeObjectURL(prev[i].preview);
    return prev.filter((_, j) => j !== i);
  });

  const shoot = async () => {
    setError('');
    setBusy('shoot');
    try {
      // The backdrop is in SCREEN_IGNORE, so the picture is the page beneath.
      const file = await captureScreen();
      addFiles([file]);
    } catch (err) {
      setError(`Screenshot failed: ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []).filter((it) => it.kind === 'file');
    if (!items.length) return;
    e.preventDefault();
    addFiles(items.map((it) => it.getAsFile()).filter(Boolean));
  };

  const context = () => {
    const orgId = (/^\/orgs\/([^/]+)/.exec(location.pathname) || [])[1] || '';
    const appId = new URLSearchParams(location.search).get('app') || '';
    return {
      orgId,
      ctx: {
        route: `${location.pathname}${location.search}`,
        appId,
        buildMainJs: currentBuild(),
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        planeEnv: IS_UAT ? 'uat' : 'prod',
      },
    };
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isBackendConfigured()) { setError('The API is not configured.'); return; }
    if (!title.trim() && description.trim().length < 8 && !(kind === 'bug' && files.length)) {
      setError(K.empty);
      return;
    }
    setBusy('send');
    try {
      const attachments = [];
      if (files.length && !isUploadConfigured()) throw new Error('Uploads are not configured.');
      for (const f of files) {
        const key = buildKey(`${K.prefix}${user.uid}/`, f.name);
        // eslint-disable-next-line no-await-in-loop
        const { key: usedKey } = await uploadFile(f.file, { key, contentType: f.type || 'application/octet-stream' });
        attachments.push({ key: usedKey, name: f.name, type: f.type, size: f.size });
      }
      const { orgId, ctx } = context();
      const out = await apiPost(K.endpoint, {
        title: title.trim(), description: description.trim(), orgId, attachments, context: ctx, website,
        name: user.displayName || '',
        ...(kind === 'feature' ? { problem: problem.trim() } : {}),
      });
      setDone(out.id);
      window.dispatchEvent(new CustomEvent(K.filedEvent, { detail: { id: out.id } }));
      setTitle('');
      setDescription('');
      setProblem('');
      setFiles([]);
    } catch (err) {
      setError(err.message || 'Could not send that.');
    } finally {
      setBusy('');
    }
  };

  if (!open) return null;

  return (
    <div
      className="bug-dialog-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) setOpen(false); }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer?.files); }}
    >
      <form className="bug-dialog" role="dialog" aria-labelledby="bug-dialog-title" onSubmit={submit} onPaste={onPaste} data-nova-ignore>
        <div className="bug-dialog-head">
          <h2 id="bug-dialog-title">{K.heading}</h2>
          <button type="button" className="bug-dialog-x" aria-label="Close" onClick={() => setOpen(false)} disabled={!!busy}>×</button>
        </div>
        {done ? (
          <div className="bug-dialog-body">
            <p className="bug-dialog-thanks"><strong>Thanks — we have it.</strong></p>
            <p className="cs-help">{K.thanks} <a href={K.thanksLink[0]}>{K.thanksLink[1]}</a>. Email goes to {user.email}.</p>
            <div className="bug-dialog-actions">
              <button type="button" className="cs-btn cs-btn-primary" onClick={() => setOpen(false)}>Done</button>
              <button type="button" className="cs-btn cs-btn-outline" onClick={() => setDone(null)}>{K.another}</button>
            </div>
          </div>
        ) : (
          <div className="bug-dialog-body">
            <p className="cs-help bug-dialog-where">
              {kind === 'feature' ? 'Requests are shared on the board across nventr; your email and organization are not shown.' : 'Page, organization, app and browser are attached automatically.'}
            </p>
            <div className="cs-field">
              <label htmlFor="bug-title">{K.titleLabel}</label>
              <input id="bug-title" ref={titleRef} className="cs-input" value={title} maxLength={160} placeholder={K.titlePlaceholder} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="cs-field">
              <label htmlFor="bug-desc">{K.descLabel}</label>
              <textarea id="bug-desc" className="cs-input" rows={5} value={description} maxLength={8000} placeholder={K.descPlaceholder} onChange={(e) => setDescription(e.target.value)} />
            </div>
            {K.problemLabel && (
              <div className="cs-field">
                <label htmlFor="bug-problem">{K.problemLabel}</label>
                <textarea id="bug-problem" className="cs-input" rows={3} value={problem} maxLength={4000} placeholder="Optional, but it helps us prioritize." onChange={(e) => setProblem(e.target.value)} />
              </div>
            )}
            <div className="bug-dialog-attach">
              {K.capture && (
                <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={shoot} disabled={!!busy || files.length >= MAX_FILES}>
                  {busy === 'shoot' ? 'Capturing…' : '📷 Capture this screen'}
                </button>
              )}
              <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => fileRef.current?.click()} disabled={!!busy || files.length >= MAX_FILES}>
                📎 Attach files
              </button>
              <input ref={fileRef} type="file" multiple accept={ACCEPT} hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
              <span className="cs-help">{K.attachHelp} · up to {MAX_FILES} · {fmtBytes(MAX_BYTES)} each</span>
            </div>
            {files.length > 0 && (
              <ul className="bug-dialog-files">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`}>
                    {f.preview ? <img src={f.preview} alt="" /> : <span className="bug-dialog-fileicon" aria-hidden="true">▤</span>}
                    <span className="bug-dialog-filename">{f.name}</span>
                    <span className="cs-help">{fmtBytes(f.size)}</span>
                    <button type="button" className="bug-dialog-x" aria-label={`Remove ${f.name}`} onClick={() => removeFile(i)} disabled={!!busy}>×</button>
                  </li>
                ))}
              </ul>
            )}
            <div className="cs-hp" aria-hidden="true">
              <label>
                Website
                <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
              </label>
            </div>
            {error && <p className="cs-land-micro" role="alert">{error}</p>}
            <div className="bug-dialog-actions">
              <button type="submit" className="cs-btn cs-btn-primary" disabled={!!busy}>
                {busy === 'send' ? 'Sending…' : K.submit}
              </button>
              <button type="button" className="cs-btn cs-btn-outline" onClick={() => setOpen(false)} disabled={!!busy}>Cancel</button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
